import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAggregator } from '../contexts/AggregatorContext'
import { getUserPreference, setUserPreference } from '../services/api'
import type { DomainOption, StageOption } from '../constants/aggregator'

// Preferences are persisted server-side via GET/PUT /users/preferences.
// localStorage (scoped by aggregator ID) acts as a write-through cache and offline fallback.

export type GroupBy = 'entity' | 'vertical' | 'stage' | 'date' | 'status'

export interface FilterState {
  domains: DomainOption[]
  stages: StageOption[]
  statuses: ('discoverable' | 'active' | 'archived')[]
  entityIds: string[]
}

export interface EntityTags {
  [entityId: string]: {
    domain?: DomainOption
    stage?: StageOption
  }
}

const DEFAULT_FILTERS: FilterState = {
  domains: [],
  stages: [],
  statuses: [],
  entityIds: [],
}

function readLS<T>(key: string, defaultValue: T): T {
  const raw = localStorage.getItem(key)
  if (!raw) return defaultValue
  try {
    return JSON.parse(raw) as T
  } catch {
    return defaultValue
  }
}

function writeLS(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value))
}

export function useAggregatorPreferences() {
  const { activeAggregatorId } = useAggregator()
  const queryClient = useQueryClient()

  const aggId = activeAggregatorId ?? ''

  // ---- Preference key builders ----
  const groupByKey = `aggregator_groupBy:${aggId}`
  const filtersKey = `aggregator_filters:${aggId}`
  const entityTagsKey = `aggregator_entity_tags:${aggId}`

  // ---- GroupBy ----
  const {
    data: groupByData,
    error: groupByError,
    isLoading: groupByLoading,
  } = useQuery({
    queryKey: ['aggregator_groupBy', aggId],
    queryFn: () => getUserPreference(groupByKey),
    enabled: !!aggId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (groupByData && !groupByError) {
      writeLS(groupByKey, groupByData.value)
    }
  }, [groupByData, groupByError, groupByKey])

  const groupBy: GroupBy = (() => {
    if (groupByData && !groupByError) return groupByData.value as GroupBy
    return readLS<GroupBy>(groupByKey, 'entity')
  })()

  const setGroupBy = async (value: GroupBy) => {
    queryClient.setQueryData(['aggregator_groupBy', aggId], { pref_key: groupByKey, value })
    writeLS(groupByKey, value)
    if (aggId) {
      await setUserPreference(groupByKey, value).catch(() => undefined)
    }
  }

  // ---- Filters ----
  const {
    data: filtersData,
    error: filtersError,
    isLoading: filtersLoading,
  } = useQuery({
    queryKey: ['aggregator_filters', aggId],
    queryFn: () => getUserPreference(filtersKey),
    enabled: !!aggId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (filtersData && !filtersError) {
      writeLS(filtersKey, filtersData.value)
    }
  }, [filtersData, filtersError, filtersKey])

  const filters: FilterState = (() => {
    if (filtersData && !filtersError) return filtersData.value as FilterState
    return readLS<FilterState>(filtersKey, DEFAULT_FILTERS)
  })()

  const setFilters = async (value: FilterState) => {
    queryClient.setQueryData(['aggregator_filters', aggId], { pref_key: filtersKey, value })
    writeLS(filtersKey, value)
    if (aggId) {
      await setUserPreference(filtersKey, value).catch(() => undefined)
    }
  }

  // ---- Entity Tags ----
  const {
    data: entityTagsData,
    error: entityTagsError,
    isLoading: entityTagsLoading,
  } = useQuery({
    queryKey: ['aggregator_entity_tags', aggId],
    queryFn: () => getUserPreference(entityTagsKey),
    enabled: !!aggId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (entityTagsData && !entityTagsError) {
      writeLS(entityTagsKey, entityTagsData.value)
    }
  }, [entityTagsData, entityTagsError, entityTagsKey])

  const entityTags: EntityTags = (() => {
    if (entityTagsData && !entityTagsError) return entityTagsData.value as EntityTags
    return readLS<EntityTags>(entityTagsKey, {})
  })()

  // setEntityTag MUST read-modify-write the full blob — never send partial update
  const setEntityTag = async (
    entityId: string,
    field: 'domain' | 'stage',
    value: DomainOption | StageOption,
  ) => {
    // Read current from cache (NOT re-fetch)
    const currentCached = queryClient.getQueryData<{ value: EntityTags }>(['aggregator_entity_tags', aggId])
    const currentTags: EntityTags = currentCached?.value ?? readLS<EntityTags>(entityTagsKey, {})

    const merged: EntityTags = {
      ...currentTags,
      [entityId]: { ...currentTags[entityId], [field]: value },
    }

    queryClient.setQueryData(['aggregator_entity_tags', aggId], { pref_key: entityTagsKey, value: merged })
    writeLS(entityTagsKey, merged)
    if (aggId) {
      await setUserPreference(entityTagsKey, merged).catch(() => undefined)
    }
  }

  const isLoading = groupByLoading || filtersLoading || entityTagsLoading

  return {
    groupBy,
    setGroupBy,
    filters,
    setFilters,
    entityTags,
    setEntityTag,
    isLoading,
  }
}
